import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import getImageUrl from "../../utils/getImageUrl";
import { useCategoriesInfiniteQuery } from "../../hooks/queries/useCategoriesInfiniteQuery";
import CategoryChildren from "./CategoryChildren";

function Categories() {
  const navigate = useNavigate();
  const [expandedIds, setExpandedIds] = useState([]);

  // Assigned roots only, paginated. The old flat `size=100` fetch grouped
  // parents and children client-side, which silently promoted a child to top
  // level whenever its parent fell outside the page.
  const {
    categories: parents,
    isLoading: loading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useCategoriesInfiniteQuery({ assigned: true, parent__isnull: true });

  const toggleExpanded = useCallback((id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  return (
    <div className="lg:py-16 py-10 bg-[#f2f2f23b]">
      <div className="mx-auto max-w-screen-2xl px-3 sm:px-10">
        <div className="mb-10 flex justify-left">
          <div className="text-left w-full lg:w-2/5">
            <div className="flex flex-row items-center gap-2">
              <svg
                focusable="false"
                aria-hidden="true"
                viewBox="0 0 24 24"
                data-testid="CategoryIcon"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="#d23f57"
              >
                <path d="M10 3H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1zm10 10h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-6a1 1 0 0 0-1-1zM17 3c-2.206 0-4 1.794-4 4s1.794 4 4 4 4-1.794 4-4-1.794-4-4-4zM7 13c-2.206 0-4 1.794-4 4s1.794 4 4 4 4-1.794 4-4-1.794-4-4-4z" />
              </svg>
              <h2 className="text-xl lg:text-3xl   font-semibold">
                Featured Categories
              </h2>
            </div>
            <p className="text-base font-sans text-gray-600 leading-6">
              Choose your necessary products from this feature categories.
            </p>
          </div>
        </div>
        {loading ? (
          <ul className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6 gap-2">
            {[...Array(6)].map((_, index) => (
              <li className="group" key={`skeleton-${index}`}>
                <div className="flex h-full w-full bg-white p-2 shadow-sm rounded-md">
                  <div className="flex flex-row gap-3 w-full justify-left items-center">
                    <div className="h-10 w-10 bg-gray-200 animate-pulse rounded-md"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 animate-pulse rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500 text-sm font-medium">Error loading categories</p>
            <p className="text-gray-500 text-xs mt-1">{error.message || 'Please try again later'}</p>
          </div>
        ) : parents.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">No categories available</p>
          </div>
        ) : (
          <>
            <ul className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6 gap-2">
              {parents.map((parentCategory) => {
                const childrenCount = parentCategory.children_count || 0;
                const hasChildren = childrenCount > 0;
                const isExpanded = expandedIds.includes(parentCategory.id);

                return (
                  <li className="group relative" key={`parent-${parentCategory.id}`}>
                    <div className="flex h-full w-full flex-col bg-white p-2 shadow-sm rounded-md transition-all duration-200 hover:shadow-lg">
                      <button
                        type="button"
                        onClick={() => navigate(`/search?Category=${parentCategory.slug}`)}
                        aria-label={`Browse ${parentCategory.name} category`}
                        className="flex w-full !no-underline text-left hover:text-brand-600 cursor-pointer hover:!text-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
                      >
                        <div className="flex flex-row gap-3 w-full justify-left items-center">
                          <img
                            src={getImageUrl(parentCategory.banner)}
                            alt={parentCategory.name}
                            className="h-10 w-10 object-cover rounded-md flex-shrink-0"
                            loading="lazy"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40"%3E%3Crect width="40" height="40" fill="%23e5e7eb"/%3E%3C/svg%3E';
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium leading-tight line-clamp-2 text-gray-700 group-hover:text-brand-600">
                              {parentCategory.name}
                            </h3>
                            {hasChildren && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {childrenCount} {childrenCount === 1 ? 'subcategory' : 'subcategories'}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                      {/* Subcategories load only when opened — see CategoryChildren */}
                      {hasChildren && (
                        <div className="mt-1 pt-2 border-t border-gray-100">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(parentCategory.id)}
                            aria-expanded={isExpanded}
                            className="text-xs text-brand-600 hover:underline"
                          >
                            {isExpanded ? 'Hide subcategories' : 'Show subcategories'}
                          </button>
                          {isExpanded && (
                            <div className="mt-2">
                              <CategoryChildren parentId={parentCategory.id} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {hasNextPage && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-4 py-2 text-sm text-brand-700 rounded-md border border-brand-300 hover:bg-brand-50 disabled:opacity-60"
                >
                  {isFetchingNextPage ? 'Loading…' : 'Load more categories'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Categories;
